import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { hasPermission, hasAnyPermission, canAccessRoute, getAccessibleNavigation, getUserPermissions, canDelete, canEdit, isAdmin } from '../utils/rbac';
import { USER_ROLES } from '../utils/constants';

// Initial state
const initialState = {
  userRole: null,
  permissions: [],
  accessibleNavigation: [],
  loading: false,
  error: null,
};

// Action types
const ROLE_ACTIONS = {
  SET_USER_ROLE: 'SET_USER_ROLE',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ROLE_DATA: 'CLEAR_ROLE_DATA',
};

/**
 * Role reducer
 * - Manages role-related state (userRole, permissions, accessible nav)
 */
const roleReducer = (state, action) => {
  switch (action.type) {
    case ROLE_ACTIONS.SET_USER_ROLE:
      const { role } = action.payload;
      return {
        ...state,
        userRole: role,
        permissions: getUserPermissions(role),
        accessibleNavigation: getAccessibleNavigation(role),
        loading: false,
        error: null,
      };

    case ROLE_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case ROLE_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };

    case ROLE_ACTIONS.CLEAR_ROLE_DATA:
      return {
        ...initialState,
      };

    default:
      return state;
  }
};

// Create context
const RoleContext = createContext();

// Role Provider Component
/**
 * RoleProvider
 * - Wraps the app and exposes role/permission utilities.
 * - `setUserRole` will compute permissions and accessible navigation based on role.
 */
export const RoleProvider = ({ children }) => {
  const [state, dispatch] = useReducer(roleReducer, initialState);

  // Set user role
  const setUserRole = useCallback((role) => {
    dispatch({
      type: ROLE_ACTIONS.SET_USER_ROLE,
      payload: { role },
    });
  }, []);

  // Set loading state
  const setLoading = useCallback((loading) => {
    dispatch({
      type: ROLE_ACTIONS.SET_LOADING,
      payload: loading,
    });
  }, []);

  // Set error
  const setError = useCallback((error) => {
    dispatch({
      type: ROLE_ACTIONS.SET_ERROR,
      payload: error,
    });
  }, []);

  // Clear role data
  const clearRoleData = useCallback(() => {
    dispatch({
      type: ROLE_ACTIONS.CLEAR_ROLE_DATA,
    });
  }, []);

  // Check if user has specific permission
  const checkPermission = useCallback((permission) => {
    return hasPermission(state.userRole, permission);
  }, [state.userRole]);

  // Check if user has any of the specified permissions
  const checkAnyPermission = useCallback((permissions) => {
    return hasAnyPermission(state.userRole, permissions);
  }, [state.userRole]);

  // Check if user can access a route
  const checkRouteAccess = useCallback((path) => {
    return canAccessRoute(state.userRole, path);
  }, [state.userRole]);

  // Check if user is super admin
  const isSuperAdmin = useCallback(() => {
    return state.userRole === USER_ROLES.SUPER_ADMIN;
  }, [state.userRole]);

  // Check if user is admin
  const isAdminCheck = useCallback(() => {
    return isAdmin(state.userRole);
  }, [state.userRole]);

  const isStaff = useCallback(() => {
    return state.userRole === USER_ROLES.SUPER_ADMIN;
  }, [state.userRole]);

  // Check if user can delete
  const checkDeleteAccess = useCallback(() => {
    return canDelete(state.userRole);
  }, [state.userRole]);

  // Check if user can edit a record
  const checkEditAccess = useCallback((recordDate, options = {}) => {
    return canEdit(state.userRole, recordDate, options);
  }, [state.userRole]);

  const contextValue = {
    // State
    ...state,

    // Actions
    setUserRole,
    setLoading,
    setError,
    clearRoleData,

    // Permission checks
    checkPermission,
    checkAnyPermission,
    checkRouteAccess,
    isSuperAdmin,
    isAdmin: isAdminCheck,
    isStaff,

    // Action-specific checks
    checkDeleteAccess,
    checkEditAccess,
  };

  return (
    <RoleContext.Provider value={contextValue}>
      {children}
    </RoleContext.Provider>
  );
};

// Custom hook to use role context
/**
 * useRole hook
 * - Throws if used outside of a RoleProvider
 */
export const useRole = () => {
  const context = useContext(RoleContext);

  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }

  return context;
};

export default RoleContext;